import { useTranslation } from 'react-i18next'
import {
  addToast,
  Button,
  ModalBody,
  ModalHeader,
  Textarea
} from '@heroui/react'
import { useTheme } from '@/ui/theme'
import { useQueryClient } from '@tanstack/react-query'
import { AnimatePresence, motion } from 'framer-motion'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import z from 'zod'
import { useSearchParams } from 'react-router'
import { usePostHog } from '@posthog/react'
import { useResizeImage } from '@/hooks/use-resize-image'
import { useUploadDocuments } from '@/hooks/use-upload-documents'
import { getTrackingInfoQuery } from '@/hooks/use-tracking-info'
import { TrashIcon } from '@/ui/icons/trash'
import { UploadImages } from './upload-images'

const MAX_TOTAL_SIZE_MB = 3.5
const MIN_REASON_LENGTH = 5

const dataUriSizeInMb = (dataUri: string) =>
  atob(dataUri.split(',')[1]).length / 1024 / 1024

const uploadPhotosSchema = z.object({
  photos: z
    .array(
      z.object({
        image: z.string(),
        description: z
          .string()
          .min(MIN_REASON_LENGTH, { message: 'upload_images.minimum_length' })
      })
    )
    .min(1)
})

type UploadPhotosFormData = z.infer<typeof uploadPhotosSchema>

interface UploadPhotosDialogProps {
  onClose: () => void
}

export const UploadPhotosDialog = ({ onClose }: UploadPhotosDialogProps) => {
  const { t } = useTranslation()

  const posthog = usePostHog()

  const [searchParams] = useSearchParams()
  const email = searchParams.get('email') ?? undefined
  const process_id = searchParams.get('processId') ?? undefined

  const theme = useTheme()
  const queryClient = useQueryClient()

  const { resizeFileAndTransformToBase64 } = useResizeImage()

  const { mutate: uploadDocuments, isPending } = useUploadDocuments()

  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors }
  } = useForm<UploadPhotosFormData>({
    resolver: zodResolver(uploadPhotosSchema),
    defaultValues: { photos: [] }
  })

  const { fields, append, remove } = useFieldArray({ control, name: 'photos' })

  const onUpload = async (inputFiles: FileList) => {
    let total = getValues('photos').reduce(
      (acc, photo) => acc + dataUriSizeInMb(photo.image),
      0
    )

    for (const file of Array.from(inputFiles)) {
      let image: string
      try {
        image = await resizeFileAndTransformToBase64(file)
      } catch {
        addToast({ description: t('upload_images.invalid_file_type') })
        return
      }

      const sizeMb = dataUriSizeInMb(image)
      if (sizeMb > MAX_TOTAL_SIZE_MB || total + sizeMb > MAX_TOTAL_SIZE_MB) {
        addToast({ description: t('upload_images.size_limit') })
        return
      }

      total += sizeMb
      append({ image, description: '' })
      posthog?.capture('Image Uploaded')
    }
  }

  const onSubmit = (data: UploadPhotosFormData) => {
    const count = data.photos.length

    posthog?.capture('Upload Photos Submitted', { count })

    uploadDocuments(
      {
        processId: process_id ?? '',
        documents: data.photos.map((photo) => ({
          document_information: photo.description,
          data: photo.image.split(',')[1]
        }))
      },
      {
        onSuccess: () => {
          onClose()
          addToast({
            color: 'success',
            description: t('upload_images.successful_upload')
          })
          queryClient.invalidateQueries({
            queryKey: getTrackingInfoQuery({ process_id, email }).queryKey
          })
        },
        onError: () => {
          addToast({ description: t('tracking_new.upload_error') })
        }
      }
    )
  }

  const color = theme.colors.primary.main

  return (
    <>
      <ModalHeader className="flex-col items-start gap-1">
        <h2 className="text-base font-medium text-neutral-900">
          {t('tracking_new.dialog_title')}
        </h2>
        <p className="text-xs font-normal text-neutral-500">
          {t('tracking_new.dialog_subtitle')}
        </p>
      </ModalHeader>
      <ModalBody className="pb-6">
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3">
          {fields.length > 0 ? (
            <div className="flex flex-col gap-3 pb-3">
              <AnimatePresence>
                {fields.map((field, index) => (
                  <motion.div
                    key={field.id}
                    layout="position"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{
                      opacity: 0,
                      scale: 0.95,
                      transition: { duration: 0.15, ease: 'easeOut' }
                    }}
                    transition={{ duration: 0.25, ease: [0.25, 1, 0.5, 1] }}
                    className="flex gap-3"
                  >
                    <div className="relative flex items-start gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          posthog?.capture('Image Removed')
                          remove(index)
                        }}
                        aria-label={t('tracking_new.remove_photo')}
                        className="absolute right-1 top-1 flex size-6 shrink-0 items-center justify-center rounded-sm bg-white text-neutral-700"
                      >
                        <TrashIcon className="size-4" />
                      </button>
                      <img
                        src={field.image}
                        alt=""
                        className="size-24 shrink-0 rounded object-cover"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <Controller
                        control={control}
                        name={`photos.${index}.description`}
                        render={({ field: descriptionField }) => (
                          <Textarea
                            {...descriptionField}
                            disableAutosize
                            radius="sm"
                            variant="bordered"
                            placeholder={t('upload_images.what_is_wrong')}
                            isInvalid={!!errors.photos?.[index]?.description}
                            errorMessage={
                              errors.photos?.[index]?.description?.message
                                ? t(errors.photos[index].description.message)
                                : undefined
                            }
                            classNames={{
                              inputWrapper: 'border-solid',
                              input: 'h-20 resize-none placeholder:text-xs'
                            }}
                          />
                        )}
                      />
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          ) : null}
          <UploadImages
            onUpload={onUpload}
            files={fields.map((field) => ({
              file: field.image,
              preview: field.image,
              description: field.description
            }))}
          />
          <Button
            type="submit"
            variant="solid"
            radius="sm"
            fullWidth
            style={{
              backgroundColor: color === 'transparent' ? 'transparent' : color,
              color: theme.colors.primary.contrastText,
              borderRadius: theme.borderRadius
            }}
            isLoading={isPending}
            isDisabled={fields.length === 0}
          >
            {t('upload_images.send')}
          </Button>
        </form>
      </ModalBody>
    </>
  )
}
